import { useState, useRef, useEffect, useMemo } from "react";
import { sortHighlightPosition } from "./Search.utils";
import type {
  CharIndex,
  HighlightArea,
  MatchIndexes,
  NormalizedKeyword
} from "./Search.types";
import { EMPTY_KEYWORD_REGEXP } from "./Search.constants";

type UseSearchProps = {
  initialKeywordRegexp?: NormalizedKeyword[];
};

export const useSearch = ({ initialKeywordRegexp }: UseSearchProps) => {
  const characterIndexesRef = useRef<{
    [pageIndex: number]: CharIndex[];
  }>({});
  const [keywordRegexp] = useState<NormalizedKeyword[]>(
    initialKeywordRegexp ?? [EMPTY_KEYWORD_REGEXP]
  );
  const [pageDimensions, setPageDimensions] = useState<{
    [pageIndex: number]: {
      height: number;
      width: number;
    };
  }>({});

  const [highlightAreasMapping, setHighlightAreasMapping] = useState<{
    [pageIndex: number]: HighlightArea[];
  }>({});
  const [, setCurrentMatch] = useState<HighlightArea | null>(null);
  const foundMatches = useMemo(
    () => Object.values(highlightAreasMapping).flat(),
    [highlightAreasMapping]
  );

  const setCharIndexes = (pageIndex: number) => {
    const spans: HTMLElement[] = getTextSpans(pageIndex);
    const charIndexes: CharIndex[] = spans
      .map(span => span.textContent || "")
      .reduce(
        (prev, curr, index) =>
          prev.concat(
            curr.split("").map((c, i) => ({
              char: c,
              charIndexInSpan: i,
              spanIndex: index
            }))
          ),
        [
          {
            char: "",
            charIndexInSpan: 0,
            spanIndex: 0
          }
        ]
      )
      .slice(1);

    characterIndexesRef.current = {
      ...characterIndexesRef.current,
      [pageIndex]: charIndexes
    };
  };
  const jumpToGivenMatch = (match: HighlightArea) => {
    const pageIndex = match.pageIndex;
    const pageElement = getPageElement(pageIndex);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const highlight = ({
    pageIndex,
    pageHeight,
    pageWidth,
    span,
    charIndexSpan
  }: {
    pageIndex: number;
    pageHeight: number;
    pageWidth: number;
    span: HTMLElement;
    charIndexSpan: CharIndex[];
  }): HighlightArea | null => {
    const firstChild = span.firstChild;
    if (!firstChild || firstChild.nodeType !== Node.TEXT_NODE) {
      return null;
    }

    const spanStyle = window.getComputedStyle(span);

    // Calculate the start and end positions of the highlighted text
    const startOffset = charIndexSpan[0].charIndexInSpan;
    const endOffset =
      charIndexSpan[charIndexSpan.length - 1].charIndexInSpan + 1;

    // Create a range to measure the width of the text before the highlight
    const range = document.createRange();
    range.setStart(firstChild, 0);
    range.setEnd(firstChild, startOffset);
    const beforeHighlightRect = range.getBoundingClientRect();

    // Create a range for the highlighted text
    range.setStart(firstChild, startOffset);
    range.setEnd(firstChild, endOffset);
    const highlightRect = range.getBoundingClientRect();

    // Calculate the left position and width of the highlight
    const left = `calc(${spanStyle.left} + ${beforeHighlightRect.width}px)`;
    const width = `${highlightRect.width}px`;

    return {
      textToHighlight: charIndexSpan.map(item => item.char).join(""),
      pageIndex,
      left,
      top: spanStyle.top,
      height: spanStyle.height,
      width,
      pageHeight,
      pageWidth
    };
  };
  const highlightAll = (
    pageIndex: number,
    keywordRegexp: NormalizedKeyword[],
    pageHeight: number,
    pageWidth: number
  ): HighlightArea[] => {
    const charIndexes = characterIndexesRef.current[pageIndex];
    if (charIndexes.length === 0) {
      return [];
    }

    const highlightPos: HighlightArea[] = [];
    const spans: HTMLElement[] = getTextSpans(pageIndex);

    // Generate the full text of page
    const fullText = charIndexes.map(item => item.char).join("");
    keywordRegexp.forEach(keyword => {
      const keywordStr = keyword.keyword;
      if (!keywordStr.trim()) {
        return;
      }

      // Clone the keyword regular expression, and add the global (`g`) flag
      // If the `g` flag is missing, it will lead to an infinitive loop
      const cloneKeyword =
        keyword.regExp.flags.indexOf("g") === -1
          ? new RegExp(keyword.regExp, `${keyword.regExp.flags}g`)
          : keyword.regExp;
      // Find all matches in the full text
      let match;
      const matches: MatchIndexes[] = [];
      while ((match = cloneKeyword.exec(fullText)) !== null) {
        matches.push({
          keyword: cloneKeyword,
          startIndex: match.index,
          endIndex: cloneKeyword.lastIndex
        });
      }

      matches
        .map(item => ({
          keyword: item.keyword,
          indexes: charIndexes.slice(item.startIndex, item.endIndex)
        }))
        .forEach(item => {
          // Group by the span index
          const spanIndexes = item.indexes.reduce(
            (acc, item) => {
              acc[item.spanIndex] = (
                acc[item.spanIndex] || ([] as CharIndex[])
              ).concat([item]);
              return acc;
            },
            {} as { [spanIndex: number]: CharIndex[] }
          );

          Object.values(spanIndexes).forEach(charIndexSpan => {
            // Ignore the space between words
            if (
              charIndexSpan.length !== 1 ||
              charIndexSpan[0].char.trim() !== ""
            ) {
              // Ignore the first and last spaces if we are finding the whole word
              const normalizedCharSpan = keyword.wholeWords
                ? charIndexSpan.slice(1, -1)
                : charIndexSpan;
              const highlightPosition = highlight({
                pageIndex,
                pageHeight,
                pageWidth,
                span: spans[normalizedCharSpan[0].spanIndex],
                charIndexSpan: normalizedCharSpan
              });
              if (highlightPosition) {
                highlightPos.push(highlightPosition);
              }
            }
          });
        });
    });

    // Sort the highlight elements as they appear in the texts
    return highlightPos.sort(sortHighlightPosition);
  };

  const onGetTextSuccessPage = (pageIndex: number) => {
    setTimeout(() => {
      const pageEl = getPageElement(pageIndex);
      const pageHeight: number = pageEl?.clientHeight || 0;
      const pageWidth: number = pageEl?.clientWidth || 0;
      setPageDimensions(prev => ({
        ...prev,
        [pageIndex]: {
          height: pageHeight,
          width: pageWidth
        }
      }));
      setCharIndexes(pageIndex);
      setHighlightAreasMapping(prev => ({
        ...prev,
        [pageIndex]: highlightAll(
          pageIndex,
          keywordRegexp,
          pageHeight,
          pageWidth
        )
      }));
    }, 1000);
  };

  useEffect(() => {
    if (foundMatches.length > 0) {
      setCurrentMatch(prevMatch => {
        const match = foundMatches[0];
        if (prevMatch?.pageIndex !== match.pageIndex) {
          jumpToGivenMatch(match);
          return match;
        }
        return prevMatch;
      });
    }
  }, [foundMatches]);

  return {
    highlightAreasMapping,
    pageDimensions,
    onGetTextSuccessPage
  };
};

const getPageElement = (pageIndex: number) => {
  return window.document.querySelector(
    `div[data-page-number="${pageIndex + 1}"]`
  );
};

const getTextSpans = (pageIndex: number) => {
  return [].slice.call(
    window.document.querySelectorAll(
      `div[data-page-number="${pageIndex + 1}"] > div.react-pdf__Page__textContent.textLayer span[role="presentation"]`
    )
  );
};
